// components/OriginsModule.js - Updated with 5 columns layout and real data
import React from 'react';
import { RefreshCw, Clock, Database, AlertTriangle } from 'lucide-react';
import { dashboardStyles } from '../dashboardStyles';

const OriginsModule = ({
                           topOrigins = [],
                           loading = false,
                           error = null,
                           lastUpdated = null,
                           cached = false,
                           cacheAge = 0,
                           dataPeriod = null,
                           autoRefresh = true,
                           onToggleAutoRefresh = () => {},
                           onRefresh = () => {},
                           isStale = false
                       }) => {

    const formatTimeAgo = (date) => {
        if (!date) return 'Never';
        const now = new Date();
        const diffMs = now - new Date(date);
        const diffMins = Math.round(diffMs / 60000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;

        const diffHours = Math.round(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;

        const diffDays = Math.round(diffHours / 24);
        return `${diffDays}d ago`;
    };

    const getStatusColor = () => {
        if (error) return '#ef4444';
        if (loading) return '#f59e0b';
        if (isStale) return '#f59e0b';
        return '#10b981';
    };

    const getStatusText = () => {
        if (error) return 'Error';
        if (loading) return 'Loading...';
        if (cached && cacheAge > 0) return `Cached (${cacheAge}m)`;
        return 'Live Data';
    };

    // Calculate items per column for 5 columns
    const itemsPerColumn = 6; // ✅ Fixed to 6 items per column
    const maxItemsToShow = 30; // 5 columns × 6 items = 30 total items

    return (
        <div style={dashboardStyles.originsModule}>
            {/* Header with status */}
            <div style={dashboardStyles.originsHeader}>
                <div style={dashboardStyles.originsIcon}>📈</div>
                <div>
                    <div style={dashboardStyles.originsTitle}>
                        Origins Sorted by Frequency
                        {dataPeriod && (
                            <span style={{
                                fontSize: '13px',
                                color: '#64748b',
                                marginLeft: '8px',
                                fontWeight: '400'
                            }}>
                                ({dataPeriod.description})
                            </span>
                        )}
                    </div>
                </div>

                {/* Status indicator and controls */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
                    {/* Status dot */}
                    <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(),
                        animation: loading ? 'pulse 2s infinite' : 'none'
                    }}></div>

                    {/* Status text */}
                    <span style={{
                        fontSize: '9px',
                        color: getStatusColor(),
                        fontWeight: '500'
                    }}>
                        {getStatusText()}
                    </span>

                    {/* Refresh button */}
                    <button
                        onClick={onRefresh}
                        disabled={loading}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#8b5cf6',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            padding: '2px',
                            borderRadius: '3px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '13px',
                            opacity: loading ? 0.5 : 1,
                            transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            if (!loading) {
                                e.target.style.backgroundColor = 'rgba(139, 92, 246, 0.1)';
                            }
                        }}
                        onMouseOut={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                        }}
                        title="Refresh data"
                    >
                        <RefreshCw
                            size={12}
                            style={{
                                animation: loading ? 'spin 1s linear infinite' : 'none'
                            }}
                        />
                    </button>
                </div>
            </div>

            {/* Last updated info */}
            {lastUpdated && (
                <div style={{
                    fontSize: '8px',
                    color: '#64748b',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}>
                    <Clock size={8} />
                    Updated {formatTimeAgo(lastUpdated)}
                </div>
            )}

            {/* Content */}
            {error ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    flexDirection: 'column',
                    gap: '8px',
                    color: '#ef4444'
                }}>
                    <AlertTriangle size={24} style={{ opacity: 0.5 }} />
                    <div style={{ fontSize: '11px', textAlign: 'center' }}>
                        Failed to load data
                    </div>
                    <button
                        onClick={onRefresh}
                        style={{
                            fontSize: '13px',
                            padding: '4px 8px',
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '4px',
                            color: '#ef4444',
                            cursor: 'pointer'
                        }}
                    >
                        Retry
                    </button>
                </div>
            ) : loading ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    flexDirection: 'column',
                    gap: '8px',
                    color: '#f59e0b'
                }}>
                    <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    <div style={{ fontSize: '11px' }}>Loading origins...</div>
                </div>
            ) : topOrigins.length === 0 ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    flexDirection: 'column',
                    gap: '8px',
                    color: '#64748b'
                }}>
                    <Database size={20} style={{ opacity: 0.5 }} />
                    <div style={{ fontSize: '11px' }}>No data available</div>
                </div>
            ) : (
                /* ✅ UPDATED: 5 columns layout instead of 3 */
                <div style={{
                    ...dashboardStyles.originsList,
                    gridTemplateColumns: 'repeat(5, 1fr)', // ✅ Changed from 3 to 5 columns
                    gap: '8px'
                }}>
                    {[0, 1, 2, 3, 4].map(columnIndex => ( // ✅ Changed from [0,1,2] to [0,1,2,3,4]
                        <div key={columnIndex} style={dashboardStyles.originsColumn}>
                            {topOrigins
                                .slice(columnIndex * itemsPerColumn, (columnIndex + 1) * itemsPerColumn)
                                .slice(0, 6) // Limit to 6 items per column
                                .map((origin, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            ...dashboardStyles.originItem,
                                            padding: '3px 5px', // ✅ Slightly increased padding
                                            minHeight: '20px'    // ✅ Slightly increased height
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.background = 'rgba(15, 23, 42, 0.8)';
                                            e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.background = 'rgba(15, 23, 42, 0.6)';
                                            e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.1)';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                        }}
                                    >
                                    <span style={{
                                        ...dashboardStyles.originName,
                                        fontSize: '13px',  // ✅ Increased from 9px to 10px
                                        maxWidth: '80px',  // ✅ Increased width slightly
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>{origin.name}</span>
                                        <span style={{
                                            ...dashboardStyles.originCount,
                                            fontSize: '13px',   // ✅ Increased from 9px to 10px
                                            minWidth: '30px'   // ✅ Increased width for better visibility
                                        }}>{origin.count.toLocaleString()}</span>
                                    </div>
                                ))}
                        </div>
                    ))}
                </div>
            )}

            {/* Show total count if we have more items than displayed */}
            {topOrigins.length > maxItemsToShow && (
                <div style={{
                    textAlign: 'center',
                    marginTop: '8px',
                    fontSize: '8px',
                    color: '#64748b',
                    fontStyle: 'italic'
                }}>
                    Showing top {Math.min(maxItemsToShow, topOrigins.length)} of {topOrigins.length} origins
                </div>
            )}

            {/* Add CSS animations */}
            <style>
                {`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
                `}
            </style>
        </div>
    );
};

export default OriginsModule;